import type { Transition, Variants } from 'motion/react';

/**
 * Параметры движения для Framer Motion.
 *
 * Те же пружины и длительности, что и в CSS-токенах (_tokens.scss):
 * компонент, анимированный из JS, и компонент, анимированный из CSS, обязаны
 * ощущаться одинаково. Меняется здесь и там одновременно.
 *
 * Пружины подобраны под тот же перелёт, что и кривые `linear()` в токенах:
 * `spring` — 3,8% (окна, панели), `snappy` — 0,5% (меню, поповеры),
 * `tight` — без перелёта (частое и мелкое).
 */
export const springs = {
  spring: { type: 'spring', stiffness: 380, damping: 26, mass: 1 },
  snappy: { type: 'spring', stiffness: 520, damping: 34, mass: 1 },
  tight: { type: 'spring', stiffness: 600, damping: 44, mass: 1 },
  /**
   * «Резиновая»: заметный перелёт и одно колебание. Для того, что переезжает
   * с места на место — индикатор вкладок, подложка под пунктом меню. Там
   * перелёт читается как инерция предмета, а не как дрожание.
   */
  rubber: { type: 'spring', stiffness: 520, damping: 22, mass: 0.9 },
} satisfies Record<string, Transition>;

/** Длительности в секундах — Framer считает в них, CSS в миллисекундах. */
export const durations = {
  instant: 0.1,
  fast: 0.15,
  base: 0.2,
  slow: 0.28,
  exitFast: 0.1,
  exitBase: 0.13,
  exitSlow: 0.18,
} as const;

/** Уход: резкий разгон без оседания, как `--ease-exit`. */
export const exitEase = [0.4, 0, 0.9, 0.6] as const;

/**
 * Перестановка элементов в списке или на доске: карточка уехала в другую
 * колонку — соседи сомкнулись. Передаётся в `layout`-анимацию.
 */
export const layoutTransition: Transition = springs.snappy;

/**
 * Появление и исчезновение элемента списка (строка, карточка, тост).
 *
 * Вход — с лёгким подъёмом и пружиной, выход — быстрее и без характера.
 * Одни и те же варианты на все списки: чтобы строка таблицы и карточка
 * канбана появлялись одинаково.
 */
export const listItem: Variants = {
  initial: { opacity: 0, y: 6, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: springs.snappy },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: durations.exitFast, ease: exitEase },
  },
};

/** Плавная смена содержимого на месте (счётчик, статус, текст кнопки). */
export const crossfade: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: durations.fast } },
  exit: { opacity: 0, transition: { duration: durations.exitFast } },
};
